// Utilities
import { defineStore } from 'pinia'
import { useFirestore, useFirebaseStorage } from 'vuefire'
import { collection, query, where, getDoc, addDoc, updateDoc, runTransaction, serverTimestamp, doc as fsDoc, onSnapshot, Unsubscribe, QuerySnapshot } from "firebase/firestore";
import type { Firestore, DocumentSnapshot } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { doc } from "firebase/firestore";
import { uid } from './utils';
import { useProjectsStore } from './projects';
import { Tenant, TenantPlan, TenantSubscriptionStatus, isCreditPlan } from '@/models/Tenant';
import type { PreferredLanguage } from '@/models/User';
import { Subscription, Invitation } from '@/models/Billing';
import { useUsersStore } from './users';
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import type { TenantRole, InvitationProjectGrant } from '@/models/Billing'
import { isEditableInvitation, invitationProjectGrants } from '@/models/Billing'
import { useAnalyticsStore } from './analytics'
import { useAppStore } from './app'
import { logger } from '@/utils/logger'
import { invitationWriteError } from '@/utils/invitationErrors'
import i18n from '@/plugins/i18n'

// localStorage persistence helpers (scoped per UID to avoid cross-user leaks).
function tenantStorageKey(userId: string): string {
  return `mac_current_tenant_id:${userId}`
}

function loadPersistedTenantId(userId: string): string {
  if (!userId) return ''
  try {
    return localStorage.getItem(tenantStorageKey(userId)) || ''
  } catch {
    return ''
  }
}

function persistTenantId(userId: string, tenantId: string): void {
  if (!userId) return
  try {
    if (tenantId) {
      localStorage.setItem(tenantStorageKey(userId), tenantId)
    } else {
      localStorage.removeItem(tenantStorageKey(userId))
    }
  } catch {
    // ignore storage errors
  }
}

export function clearPersistedTenantId(userId: string): void {
  if (!userId) return
  try {
    localStorage.removeItem(tenantStorageKey(userId))
  } catch {
    // ignore
  }
}

export interface CrossTenantPrompt {
  projectId: string
  projectName: string
  targetTenantId: string
  targetTenantName: string
  redirectTo: string
}

/**
 * MYAC-1176 — read-modify-write of an invitation's project grants, atomically.
 *
 * Three screens write this field now, so a plain read-then-update would let two
 * admins editing the same invitation at the same moment overwrite each other,
 * and a grant lost that way is a person quietly missing a project when they
 * accept. The transaction re-reads the document server-side and abandons the
 * write if it moved underneath, so the merge always runs on current grants.
 *
 * `nextGrants` receives the invitation as it stands inside the transaction and
 * returns the grants it should end up with. Every caller reaches it through
 * `updateInvitationProjects`, which merges per-project decisions onto that
 * read — there is deliberately no path that writes a list computed before the
 * transaction opened.
 *
 * Attribution: an untouched grant keeps whoever authorised it; anything new or
 * re-roled becomes the caller's, and the backend re-checks each grant against
 * its author when the invitee accepts.
 */
async function applyInvitationGrants(
  db: Firestore,
  invitationId: string,
  actorUid: string,
  nextGrants: (current: Invitation) => InvitationProjectGrant[],
): Promise<number> {
  const ref = fsDoc(db, 'invitations', invitationId)
  return runTransaction(db, async (tx) => {
    const invitation = Invitation.fromFirebase(await tx.get(ref) as DocumentSnapshot)
    if (!invitation) {
      throw invitationWriteError('invitation_not_found', `invitation ${invitationId} not found`)
    }
    if (!isEditableInvitation(invitation)) {
      throw invitationWriteError(
        'invitation_not_editable',
        `invitation ${invitationId} is ${invitation.status} and can no longer be edited`,
      )
    }

    const previousRoleById = new Map(invitationProjectGrants(invitation).map((g) => [g.id, g.role]))
    const previousGranters = invitation.projects_granted_by || {}

    const granted = nextGrants(invitation).filter((p) => p.role === 'member' || p.role === 'admin')
    const projects_granted_by: Record<string, string> = {}
    granted.forEach((p) => {
      const unchanged = previousRoleById.get(p.id) === p.role && previousGranters[p.id]
      projects_granted_by[p.id] = unchanged ? previousGranters[p.id] : actorUid
    })

    tx.update(ref, {
      projects_member: granted.filter((p) => p.role === 'member').map((p) => fsDoc(db, `projects/${p.id}`)),
      projects_admin: granted.filter((p) => p.role === 'admin').map((p) => fsDoc(db, `projects/${p.id}`)),
      projects_granted_by,
      updated_at: serverTimestamp(),
    })
    return granted.length
  })
}

/**
 * Guards, runs and reports one grant write. Both store actions go through it so
 * the sign-in guard, the CSM action and the analytics pair cannot drift apart
 * between the "replace all" and "add one" callers.
 */
async function runInvitationGrantWrite(
  invitationId: string,
  nextGrants: (current: Invitation) => InvitationProjectGrant[],
): Promise<void> {
  if (!invitationId) {
    return Promise.reject(new Error('invitationId is required'))
  }
  const actorUid = useUsersStore().uid
  if (!actorUid) {
    return Promise.reject(new Error('a signed-in user is required'))
  }
  try {
    const count = await applyInvitationGrants(useFirestore(), invitationId, actorUid, nextGrants)
    try { trackAction(CsmAction.TeamInvitationProjectsUpdated) } catch { }
    try { useAnalyticsStore().trackEvent('invitation_projects_update_success', { count }) } catch { }
  } catch (error) {
    logger.error('Error updating invitation projects', error)
    try {
      useAnalyticsStore().trackEvent('invitation_projects_update_error', { message: (error as Error)?.message })
    } catch { }
    return Promise.reject(error)
  }
}

export const useTenantsStore = defineStore('tenants', {
  state: () => ({
    loadedOnce: false,
    tenants: [] as Tenant[],
    // Explicit primary state for the active tenant (persisted across reloads).
    // Replaces the previous derivation from currentProject.tenant.path.
    currentTenantId: '' as string,
    subscriptions: null as Subscription | null,
    invitations: [] as Invitation[],
    invitationsUnsub: null as Unsubscribe | null,
    tenantsUnsub: null as Unsubscribe | null,
    // Set by the cross-tenant route guard when the user navigates to a project
    // that lives in another tenant. A global dialog watches this state and
    // offers to switch. Cleared on confirm/cancel.
    crossTenantPrompt: null as CrossTenantPrompt | null,
  }),
  getters: {
    tenantsAdmin: (state) => state.tenants?.filter((t) => t.admins?.indexOf(uid()) >= 0),
    currentTenant(state): Tenant | null {
      return state.tenants?.find((t) => t.id === state.currentTenantId) || null
    },
    subscriptionRequiresAttention(): boolean {
      const tenant = this.currentTenant
      if (!tenant?.subscription_data) return false
      return tenant.subscription_data.requiresAttention()
    },
    subscriptionStatusInfo(): {
      status: TenantSubscriptionStatus | string | null
      severity: 'success' | 'info' | 'warn' | 'error'
      messageKey: string
      actionRequired: 'update_payment' | 'add_payment' | 'authenticate' | 'manage' | null
      requiresAttention: boolean
    } | null {
      const tenant = this.currentTenant
      if (!tenant?.subscription_data) return null
      return {
        status: tenant.subscription_data.status,
        severity: tenant.subscription_data.getStatusSeverity(),
        messageKey: tenant.subscription_data.getStatusMessageKey(),
        actionRequired: tenant.subscription_data.getActionRequired(),
        requiresAttention: tenant.subscription_data.requiresAttention(),
      }
    },
  },
  actions: {
    stopTenantsSubscription: function (): void {
      if (this.tenantsUnsub) {
        this.tenantsUnsub()
        this.tenantsUnsub = null
      }
    },
    listTenants: async function (forceUpdate = false) {
      try {
        logger.debug("Listing tenants", forceUpdate)
        const uid = useUsersStore().uid;
        if (!uid) {
          logger.error("No user uid to list tenants")
          this.stopTenantsSubscription()
          return
        }

        // If already subscribed and not forcing update, skip
        if (this.tenantsUnsub && !forceUpdate) {
          return Promise.resolve()
        }

        // Clean up existing subscription if forcing update
        if (forceUpdate) {
          this.stopTenantsSubscription()
        }

        logger.debug("Subscribing to tenants for user", uid)
        const db = useFirestore()
        const q = query(collection(db, "tenants"), where("members", 'array-contains', uid))

        this.tenantsUnsub = onSnapshot(q, (snap: QuerySnapshot) => {
          try {
            this.tenants = snap.docs
              .map((d) => Tenant.fromFirebase(d))
              .filter((t): t is Tenant => t !== null)

            // Resolve the active tenant id explicitly. If the previously selected
            // tenant disappears (user removed from it, deletion, etc.), drop it
            // so we can pick a valid replacement below.
            if (this.currentTenantId && !this.tenants.some((t) => t.id === this.currentTenantId)) {
              this.currentTenantId = ''
              if (this.tenants.length > 1) {
                const replacement = this.tenants[0]
                persistTenantId(uid, replacement.id)
                this.switchTenant(replacement.id).catch((e) =>
                  logger.warn('auto-switch after tenant removal failed', e)
                )
                return
              }
            }
            if (!this.currentTenantId && this.tenants.length > 0) {
              const persisted = loadPersistedTenantId(uid)
              const persistedExists = persisted && this.tenants.some((t) => t.id === persisted)
              if (persistedExists) {
                this.currentTenantId = persisted
                persistTenantId(uid, persisted)
              } else if (this.tenants.length === 1) {
                // Single-tenant users skip the picker entirely.
                this.currentTenantId = this.tenants[0].id
                persistTenantId(uid, this.tenants[0].id)
              }
              // 2+ tenants without a persisted choice: leave currentTenantId empty
              // and rely on the home guard to redirect to /choose-tenant.
            }

            // Load subscription for current tenant if available
            if (this.currentTenant?.id && !this.loadedOnce) {
              this.loadSubscription()
            }

            // Auto-poll credit balance for credit-plan tenants
            if (this.currentTenant?.id && isCreditPlan(this.currentTenant.plan)) {
              const tenantId = this.currentTenant.id
              import('./billing').then(({ useBillingStore }) => {
                useBillingStore().startCreditBalancePolling(tenantId)
              })
            }

            if (!this.loadedOnce) {
              this.loadedOnce = true
              try {
                const subscriptions = this.subscriptions
                const status = subscriptions?.status || null
                if (status) useAnalyticsStore().trackEvent('subscription_loaded', { status })
              } catch { }
            }
          } catch (e) {
            logger.error('Error handling tenants snapshot', e)
          }
        }, (error) => {
          logger.error('Error in tenants subscription', error)
          try {
            useAppStore().toast(i18n.global.t('tenant.error_title'), i18n.global.t('tenant.error_updating_data'), 'error', 4000)
          } catch {
            // ignore toast errors
          }
        })
      } catch (error) {
        logger.error("Error subscribing to tenants", error);
        return Promise.reject(error);
      }
    },
    loadSubscription: async function () {
      try {
        if (!this.currentTenant || !this.currentTenant.subscription_data?.subscription_id) return
        // Only tenant admins are allowed to read subscriptions per security rules
        const currentUid = useUsersStore().uid
        const isAdmin = Array.isArray(this.currentTenant.admins) && this.currentTenant.admins.indexOf(currentUid) >= 0
        if (!isAdmin) {
          // Non-admins are not allowed to read subscriptions; skip silently
          return
        }
        const q = doc(useFirestore(), "tenants", this.currentTenant.id, "subscriptions", this.currentTenant.subscription_data.subscription_id)
        this.subscriptions = await getDoc(q).then((r) => Subscription.fromFirebase(r))
      } catch (error) {
        // Swallow permission errors so listing tenants doesn't fail for non-admins
        logger.warn('Unable to load subscription for current tenant', error)
      }
    },
    subscribeToInvitations: async function () {
      try {
        if (this.invitationsUnsub) {
          this.invitationsUnsub()
          this.invitationsUnsub = null
        }
        if (!this.currentTenant?.id) return
        const db = useFirestore()
        const ref = query(
          collection(db, `invitations`),
          where('tenant_id', '==', this.currentTenant.id)
        )
        this.invitationsUnsub = onSnapshot(ref, (snap) => {
          this.invitations = snap.docs
            .map((d) => Invitation.fromFirebase(d))
            .filter((x): x is Invitation => x !== null)
        })
      } catch (error) {
        logger.error('Error subscribing to invitations', error)
      }
    },
    unsubscribeInvitations: function () {
      if (this.invitationsUnsub) {
        this.invitationsUnsub()
        this.invitationsUnsub = null
      }
    },
    inviteUser: async function (tenantId: string, payload: { email: string; isTenantAdmin: boolean; projects: InvitationProjectGrant[] }) {
      try {
        if (!tenantId) {
          logger.error('inviteUser: tenantId is required')
          return
        }
        const db = useFirestore()
        const usersStore = useUsersStore()
        const ref = collection(db, 'invitations')

        const granted = payload.projects.filter((p) => p.role === 'member' || p.role === 'admin')
        const projects_member = granted.filter((p) => p.role === 'member').map((p) => fsDoc(db, `projects/${p.id}`))
        const projects_admin = granted.filter((p) => p.role === 'admin').map((p) => fsDoc(db, `projects/${p.id}`))
        // MYAC-1176 — stamp the author of every grant made at creation time, so
        // that grants added later by another admin can be told apart and checked
        // against the admin who actually made them.
        const projects_granted_by: Record<string, string> = {}
        granted.forEach((p) => { projects_granted_by[p.id] = usersStore.uid })

        const invite = {
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          tenant_id: tenantId,
          tenant_name: this.currentTenant?.name || '',
          created_by: usersStore.uid,
          created_by_email: usersStore.user?.email || '',
          status: 'requested',
          email: payload.email.trim().toLowerCase(),
          tenant_role: payload.isTenantAdmin ? 'admin' as TenantRole : 'member' as TenantRole,
          projects_member,
          projects_admin,
          projects_granted_by,
        }
        logger.debug("invite", invite)
        // MYAC-1291 — the id is the caller's only handle on the outcome. The
        // verdict is not in this promise: it resolves as soon as the document
        // is written, and billing decides `pending` vs `failed` afterwards.
        const created = await addDoc(ref, invite)
        try { trackAction(CsmAction.TeamMemberInvited) } catch { }
        return created.id
      } catch (error) {
        logger.error('Error creating user invitation', error)
        return Promise.reject(error)
      }
    },
    /**
     * MYAC-1176 — change what projects a still-open invitation grants.
     *
     * The invitee does not exist as a member yet, so there is no uid to hang a
     * `member_management_request` on: the grant lives on the invitation until
     * they accept, and the backend turns it into real memberships at that
     * point. Editing the invitation in place is therefore the whole mechanism.
     *
     * `changes` is a set of PER-PROJECT decisions, never a full list, and it is
     * merged onto the invitation as read inside the transaction — a project not
     * named is left exactly as it is. A full replacement computed on the client
     * would silently drop whatever another admin granted between that read and
     * this write, which is the one failure nobody would notice: the invitee
     * simply never gets the project. `none` removes a project.
     */
    // csm-event: none — TeamInvitationProjectsUpdated is emitted by
    // runInvitationGrantWrite, which every write here goes through; tracking it
    // again at this level would count one edit twice.
    updateInvitationProjects: async function (invitationId: string, changes: InvitationProjectGrant[]): Promise<void> {
      if (!changes.length) return
      return runInvitationGrantWrite(invitationId, (current) => {
        const byId = new Map(invitationProjectGrants(current).map((g) => [g.id, g.role]))
        changes.forEach((change) => {
          if (change.role === 'none') byId.delete(change.id)
          else byId.set(change.id, change.role)
        })
        return Array.from(byId, ([id, role]) => ({ id, role }))
      })
    },
    /**
     * MYAC-1176 — attach one project to a still-open invitation, keeping every
     * grant it already carries. Used by the project member dialog and by
     * project creation; a project the invitation already carries is re-roled.
     */
    grantProjectToInvitation: async function (invitationId: string, projectId: string, role: 'member' | 'admin'): Promise<void> {
      if (!projectId) {
        return Promise.reject(new Error('projectId is required'))
      }
      return this.updateInvitationProjects(invitationId, [{ id: projectId, role }])
    },
    /**
     * MYAC-1176 — take one project back off a still-open invitation, leaving
     * the rest of its grants alone.
     */
    revokeProjectFromInvitation: async function (invitationId: string, projectId: string): Promise<void> {
      if (!projectId) {
        return Promise.reject(new Error('projectId is required'))
      }
      return this.updateInvitationProjects(invitationId, [{ id: projectId, role: 'none' }])
    },
    setCurrentTenantId(tenantId: string): void {
      if (this.currentTenantId === tenantId) return
      this.currentTenantId = tenantId
      const userId = useUsersStore().uid
      if (userId) persistTenantId(userId, tenantId)
    },
    async initTenantScopedListeners(): Promise<void> {
      if (!this.currentTenant?.id) return
      try { await this.subscribeToInvitations() } catch (e) { logger.warn('initTenantScopedListeners: invitations failed', e) }
      try { await this.loadSubscription() } catch (e) { logger.warn('initTenantScopedListeners: subscription failed', e) }
      if (isCreditPlan(this.currentTenant.plan)) {
        try {
          const { useBillingStore } = await import('./billing')
          useBillingStore().startCreditBalancePolling(this.currentTenant.id)
        } catch (e) { logger.warn('initTenantScopedListeners: credit polling failed', e) }
      }
    },
    /**
     * Switches the active tenant. This is a hard reset: it tears down all
     * project-scoped Firestore subscriptions, clears tenant- and project-scoped
     * state across stores, then re-fetches the new tenant's projects and
     * re-arms tenant-level listeners. Callers are expected to redirect the
     * user (typically to /dashboard) after this resolves.
     *
     * Hard reset is preferred over fine-grained reactivity because tenant
     * switching is a rare action and stale subscriptions across many stores
     * would be the source of subtle bugs. See tenant-aware design doc.
     */
    switchTenant: async function (tenantId: string): Promise<void> {
      try {
        if (!tenantId) {
          logger.error('switchTenant: tenantId is required')
          return
        }
        if (tenantId === this.currentTenantId) return
        const target = this.tenants.find((t) => t.id === tenantId)
        if (!target) {
          logger.error('switchTenant: tenant not found in user tenants', tenantId)
          return
        }

        // 1) Stop tenant-scoped listeners owned by this store
        this.unsubscribeInvitations()

        // 2) Stop billing listeners + polling. Imported dynamically to avoid
        //    circular imports across stores during initial bootstrap.
        try {
          const { useBillingStore } = await import('./billing')
          const billing = useBillingStore()
          billing.stopCreditBalancePolling()
          if (typeof billing.stopPlanSwitchSubscription === 'function') {
            billing.stopPlanSwitchSubscription()
          }
          if (billing.checkoutSessionsUnsub) {
            try { billing.checkoutSessionsUnsub() } catch { /* ignore */ }
            billing.checkoutSessionsUnsub = null
          }
          if (billing.checkoutSessionRequestsUnsub) {
            try { billing.checkoutSessionRequestsUnsub() } catch { /* ignore */ }
            billing.checkoutSessionRequestsUnsub = null
          }
          if (billing.projectCreationRequestsUnsub) {
            try { billing.projectCreationRequestsUnsub() } catch { /* ignore */ }
            billing.projectCreationRequestsUnsub = null
          }
          if (billing.tenantCreationRequestsUnsub) {
            try { billing.tenantCreationRequestsUnsub() } catch { /* ignore */ }
            billing.tenantCreationRequestsUnsub = null
          }
        } catch (e) {
          logger.warn('switchTenant: billing cleanup failed', e)
        }

        // 3) Clear members listeners (tenant + project scoped)
        try {
          const { useMembersStore } = await import('./members')
          const members = useMembersStore()
          members.clearTenantMembers()
          members.clearProjectMembers()
        } catch (e) {
          logger.warn('switchTenant: members cleanup failed', e)
        }

        // 4) Tear down all project-scoped subscriptions across stores. Each
        //    store exposes its own teardown method; we orchestrate them here
        //    to keep this the single tenant-switch entry point.
        try {
          const { useWidgetsStore } = await import('./widgets')
          useWidgetsStore().allWidgets_unsub()
        } catch (e) { logger.warn('switchTenant: widgets cleanup failed', e) }

        try {
          const { useAssessmentsStore } = await import('./assessments')
          const a = useAssessmentsStore()
          if (typeof a.assessments_unsub === 'function') a.assessments_unsub()
          if (typeof a.coherenceChecksUnsub === 'function') a.coherenceChecksUnsub()
        } catch (e) { logger.warn('switchTenant: assessments cleanup failed', e) }

        try {
          const { useDatasourcesStore } = await import('./datasources')
          const ds = useDatasourcesStore()
          if (ds.unsubscribe) {
            try { ds.unsubscribe() } catch { /* ignore */ }
            ds.unsubscribe = null
          }
          if (typeof ds.clearFoldersCache === 'function') ds.clearFoldersCache()
          ds.datasources = []
          ds.currentDatasourceId = ''
          ds.currentProjectId = ''
          ds.loadedOnce = false
        } catch (e) { logger.warn('switchTenant: datasources cleanup failed', e) }

        try {
          const { useInternalAuditsStore } = await import('./internalAudits')
          const ia = useInternalAuditsStore()
          if ('_cleanupSubscriptions' in ia && typeof ia._cleanupSubscriptions === 'function') ia._cleanupSubscriptions()
        } catch (e) { logger.warn('switchTenant: internalAudits cleanup failed', e) }

        try {
          const { useRegulatoryMonitoringStore } = await import('./regulatoryMonitoring')
          const rm = useRegulatoryMonitoringStore()
          if ('cleanup' in rm && typeof rm.cleanup === 'function') rm.cleanup()
        } catch (e) { logger.warn('switchTenant: regulatoryMonitoring cleanup failed', e) }

        try {
          const { useProcessesStore } = await import('./processes')
          useProcessesStore().processes_unsub()
        } catch (e) { logger.warn('switchTenant: processes cleanup failed', e) }

        try {
          const { useChatbotStore } = await import('./chatbot')
          await useChatbotStore().resetSession()
        } catch (e) { logger.warn('switchTenant: chatbot reset failed', e) }

        try {
          const { useJobsStore } = await import('./jobs')
          useJobsStore().jobs_unsub()
        } catch (e) { logger.warn('switchTenant: jobs cleanup failed', e) }

        try {
          const { useTDCurrentStore } = await import('./tdCurrent')
          const tdc = useTDCurrentStore()
          if ('clear' in tdc && typeof tdc.clear === 'function') tdc.clear()
        } catch (e) { logger.warn('switchTenant: tdCurrent cleanup failed', e) }

        try {
          const { useTDSectionStore } = await import('./tdSection')
          const tds = useTDSectionStore()
          if ('clear' in tds && typeof tds.clear === 'function') tds.clear()
        } catch (e) { logger.warn('switchTenant: tdSection cleanup failed', e) }

        // 5) Reset the projects store: drop currentProjectId, project list and
        //    the project creation request subscription so the new tenant starts
        //    from a clean slate.
        try {
          const projectsStore = useProjectsStore()
          if (projectsStore._projectCreationRequestUnsub) {
            try { projectsStore._projectCreationRequestUnsub() } catch { /* ignore */ }
            projectsStore._projectCreationRequestUnsub = null
          }
          projectsStore.projectCreationRequestSnapshot = null
          projectsStore.currentProjectId = ''
          projectsStore.projects = []
          projectsStore.loadedOnce = false
        } catch (e) {
          logger.warn('switchTenant: projects reset failed', e)
        }

        // 6) Switch the active tenant id (and persist) BEFORE re-fetching so
        //    any reactive consumer sees the new id once data lands.
        this.setCurrentTenantId(tenantId)
        this.subscriptions = null

        // 7) Re-fetch projects and re-arm tenant-level listeners.
        try {
          const projectsStore = useProjectsStore()
          await projectsStore.listProjects(true)
        } catch (e) { logger.warn('switchTenant: project re-fetch failed', e) }

        try {
          const { useWidgetsStore } = await import('./widgets')
          await useWidgetsStore().subscribeAllWidgets(true)
        } catch (e) { logger.warn('switchTenant: widgets re-subscribe failed', e) }

        try {
          await this.subscribeToInvitations()
        } catch (e) { logger.warn('switchTenant: invitations re-subscribe failed', e) }

        try {
          await this.loadSubscription()
        } catch (e) { logger.warn('switchTenant: subscription reload failed', e) }

        if (target.id && isCreditPlan(target.plan)) {
          try {
            const { useBillingStore } = await import('./billing')
            useBillingStore().startCreditBalancePolling(target.id)
          } catch (e) { logger.warn('switchTenant: credit polling restart failed', e) }
        }

        try {
          useAnalyticsStore().trackEvent('tenant_switched', { tenant_id: target.id })
        } catch { /* ignore analytics errors */ }
      } catch (error) {
        logger.error('switchTenant: unexpected error', error)
      }
    },
    setCrossTenantPrompt(prompt: CrossTenantPrompt): void {
      this.crossTenantPrompt = prompt
    },
    clearCrossTenantPrompt(): void {
      this.crossTenantPrompt = null
    },
    cancelInvitation: async function (invitationId: string) {
      try {
        if (!invitationId) {
          logger.error('cancelInvitation: invitationId is required')
          return
        }
        const db = useFirestore()
        const ref = fsDoc(db, 'invitations', invitationId)
        await updateDoc(ref, {
          status: 'cancel',
          updated_at: serverTimestamp(),
        })
        try { trackAction(CsmAction.TeamInvitationCancelled) } catch { }
        try {
          useAnalyticsStore().trackEvent('invitation_cancel_success')
        } catch { }
        try {
          useAppStore().toast(
            i18n.global.t('settings.users.cancel_invitation_success'),
            '',
            'success',
            3000,
          )
        } catch { }
      } catch (error) {
        logger.error('Error cancelling invitation', error)
        try {
          useAnalyticsStore().trackEvent('invitation_cancel_error', { message: (error as Error)?.message })
        } catch { }
        try {
          useAppStore().toast(
            i18n.global.t('settings.users.cancel_invitation_error'),
            (error as Error)?.message || '',
            'error',
            4000,
          )
        } catch { }
      }
    },
    // --- Company language (MYAC-1323) ---
    // No success toast: the live tenant listener echoes the saved value back into
    // currentTenant, which is what the settings select shows.
    // csm-event: none — a one-off company setting, not engagement.
    async updateTenantLocale(tenantId: string, locale: PreferredLanguage): Promise<void> {
      if (!tenantId) {
        return Promise.reject(new Error('tenantId is required'))
      }
      try {
        await updateDoc(fsDoc(useFirestore(), 'tenants', tenantId), {
          locale,
          updated_at: serverTimestamp(),
        })
      } catch (error) {
        logger.error('Error updating company language', error)
        try {
          useAppStore().toast(
            i18n.global.t('settings.company.language_save_error'),
            '',
            'error',
            5000,
          )
        } catch { }
        return Promise.reject(error)
      }
    },
    // --- Company logo (MYAC-866) ---
    // Upload/replace the company logo. The file is stored at the fixed Storage
    // path tenants/{id}/logo (so replace overwrites) and its download URL is
    // denormalised onto the tenant doc. The live onSnapshot listener refreshes
    // currentTenant automatically, so the new logo appears app-wide.
    async uploadTenantLogo(tenantId: string, file: File): Promise<void> {
      if (!tenantId) {
        return Promise.reject(new Error('tenantId is required'))
      }
      try {
        const storage = useFirebaseStorage()
        const fileRef = storageRef(storage, `tenants/${tenantId}/logo`)
        await uploadBytes(fileRef, file, {
          contentType: file.type,
          cacheControl: 'public, max-age=31536000, immutable',
        })
        const url = await getDownloadURL(fileRef)
        const db = useFirestore()
        await updateDoc(fsDoc(db, 'tenants', tenantId), {
          logo_url: url,
          updated_at: serverTimestamp(),
        })
        try { useAnalyticsStore().trackEvent('company_logo_uploaded') } catch { }
      } catch (error) {
        logger.error('Error uploading company logo', error)
        return Promise.reject(error)
      }
    },
    async removeTenantLogo(tenantId: string): Promise<void> {
      if (!tenantId) {
        return Promise.reject(new Error('tenantId is required'))
      }
      try {
        const storage = useFirebaseStorage()
        const fileRef = storageRef(storage, `tenants/${tenantId}/logo`)
        try {
          await deleteObject(fileRef)
        } catch (error) {
          // The object may already be gone — clearing the URL below is what matters.
          logger.warn('Company logo object delete skipped', error)
        }
        const db = useFirestore()
        await updateDoc(fsDoc(db, 'tenants', tenantId), {
          logo_url: null,
          updated_at: serverTimestamp(),
        })
        try { useAnalyticsStore().trackEvent('company_logo_removed') } catch { }
      } catch (error) {
        logger.error('Error removing company logo', error)
        return Promise.reject(error)
      }
    },
  }
})
